// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/ShadowingCiego.jsx
//  FORMATO 3 — SHADOWING A CIEGAS
//
//  Escuchas una línea a velocidad real con el texto difuminado. Presionas y la
//  imitas encima: velocidad, pausas y ritmo.
//
//  ── LA PEDAGOGÍA ────────────────────────────────────────────────────────────
//  El shadowing es el secreto de los políglotas. Entrena prosodia —la música
//  del idioma— y memoria muscular de la boca. Lo que hace que alguien suene
//  fluido no es pronunciar vocales aisladas: es unir palabras (connected
//  speech) al ritmo correcto. Por eso el texto arranca borroso: si lo lees, lo
//  pronuncias como se escribe, no como suena.
//
//  ── QUÉ MIDE ESTA PANTALLA, Y QUÉ NO ────────────────────────────────────────
//  El reconocedor del navegador NORMALIZA el acento: devuelve palabras bien
//  escritas aunque la pronunciación sea imperfecta. Con ese instrumento se
//  puede medir honestamente dos cosas:
//
//     · PALABRAS  — cuánto del contenido reprodujiste.
//     · RITMO     — tu duración contra la duración nativa de referencia.
//                   Hablar demasiado lento es el síntoma número uno de estar
//                   traduciendo en la cabeza.
//
//  Lo que NO se puede medir aquí es el acento, y la pantalla no finge lo
//  contrario: nunca dice "sonaste como un nativo de Nueva York". Un veredicto
//  de acento real necesita análisis de audio (Deepgram / `transcription.py`,
//  Zona Roja) y es una decisión aparte.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';

import { speak, cancelSpeech, isSpeechAvailable, createRecognition, isRecognitionAvailable }
  from '../utils/browserSpeech';
import { SHADOWING, byContext, shuffle, normalize } from './dojoBank';
import { fxHit, fxCombo, fxMiss, fxStart } from '../audio/fx';
import { useFeedback } from './useFeedback';
import FeedbackLayer from './FeedbackLayer';
import DojoResult from './DojoResult';

const ROUNDS = 6;
const MAX_REC_MS = 9000;

/** Palabras de la referencia que aparecen en lo que dijo el alumno. */
function wordMatch(said, target) {
  const want = normalize(target).split(' ').filter(Boolean);
  const got = normalize(said).split(' ').filter(Boolean);
  if (!want.length) return 0;

  const pool = [...got];
  let hit = 0;
  want.forEach((w) => {
    const at = pool.indexOf(w);
    if (at >= 0) { hit += 1; pool.splice(at, 1); }
  });
  return Math.round((hit / want.length) * 100);
}

/**
 * Ritmo: 100 si tardaste lo mismo que la referencia; baja al alejarte.
 * Se penaliza más ir lento que ir rápido — ir rápido de más rara vez es el
 * problema de un hispanohablante, ir lento siempre lo es.
 */
function tempoScore(actualS, targetS) {
  if (!actualS || !targetS) return 0;
  const ratio = actualS / targetS;
  const off = ratio > 1 ? (ratio - 1) / 1.1 : (1 - ratio) / 0.75;
  return Math.max(0, Math.round((1 - off) * 100));
}

export default function ShadowingCiego({ ctx, onExit }) {
  const [deck] = useState(() => shuffle(byContext(SHADOWING, ctx)).slice(0, ROUNDS));
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState('intro');  // intro|ready|playing|live|scored|done
  const [heard, setHeard] = useState('');
  const [scores, setScores] = useState([]);     // {words, tempo, total}
  const [current, setCurrent] = useState(null);
  const [plays, setPlays] = useState(0);

  const { flash, floats, fire } = useFeedback();

  const recRef = useRef(null);
  const startedAt = useRef(0);
  const stopTimer = useRef(null);

  const item = deck[i];
  const canSpeak = isSpeechAvailable();
  const canHear = isRecognitionAvailable();

  const cleanup = useCallback(() => {
    cancelSpeech();
    try { recRef.current?.stop(); } catch { /* ya detenido */ }
    recRef.current = null;
    clearTimeout(stopTimer.current);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  /* ── Reproducir la referencia ──────────────────────────────────────────── */
  const play = () => {
    if (!canSpeak || phase === 'playing' || phase === 'live') return;
    setPhase('playing');
    setPlays((p) => p + 1);
    // rate 1.05: velocidad de conversación real, no de dictado.
    speak(item.text, { rate: 1.05 }, {
      onEnd: () => setPhase('ready'),
      onError: () => setPhase('ready'),
    });
  };

  /* ── Grabar la imitación ───────────────────────────────────────────────── */
  const finish = useCallback((text) => {
    const secs = (performance.now() - startedAt.current) / 1000;
    const words = wordMatch(text, item.text);
    const tempo = tempoScore(secs, item.seconds);
    // Las palabras pesan más: sin contenido no hay ritmo que valga.
    const total = Math.round(words * 0.65 + tempo * 0.35);

    setCurrent({ words, tempo, total, secs });
    setScores((s) => [...s, { words, tempo, total }]);
    setPhase('scored');

    if (total >= 85) { fxCombo(4); fire(true, `${total}%`); }
    else if (total >= 60) { fxHit(); fire(true, `${total}%`); }
    else { fxMiss(); fire(false, `${total}%`); }
  }, [item, fire]);

  const record = () => {
    if (!canHear) { finish(''); return; }

    setHeard('');
    setPhase('live');
    startedAt.current = performance.now();

    const rec = createRecognition({
      onPartial: setHeard,
      onFinal: (text) => { setHeard(text); finish(text); },
      onError: () => finish(''),
    });
    recRef.current = rec;
    rec?.start();

    stopTimer.current = setTimeout(() => {
      try { recRef.current?.stop(); } catch { /* onFinal cierra */ }
    }, MAX_REC_MS);
  };

  const stopNow = () => {
    clearTimeout(stopTimer.current);
    try { recRef.current?.stop(); } catch { /* onFinal cierra */ }
  };

  const next = () => {
    cleanup();
    setHeard('');
    setCurrent(null);
    setPlays(0);
    if (i + 1 >= deck.length) { setPhase('done'); return; }
    setI((n) => n + 1);
    setPhase('ready');
  };

  const begin = () => { fxStart(); setPhase('ready'); };

  /* ── Render ────────────────────────────────────────────────────────────── */
  if (phase === 'done') {
    const avg = (k) => Math.round(scores.reduce((a, s) => a + s[k], 0) / (scores.length || 1));
    return (
      <DojoResult
        format="shadowing"
        score={avg('total')}
        total={deck.length}
        headline={`${avg('total')}%`}
        label="Match promedio"
        recap={[
          { v: `${avg('words')}%`, k: 'palabras' },
          { v: `${avg('tempo')}%`, k: 'ritmo' },
          { v: deck.length, k: 'líneas' },
        ]}
        note={avg('tempo') < 65
          ? 'Tu ritmo va por debajo del nativo. Repite la misma línea tres veces seguidas: la boca aprende antes que la cabeza.'
          : 'Vas al ritmo del original. Eso es connected speech — el rasgo que más hace sonar fluido.'}
        onAgain={() => window.location.reload()}
        onExit={onExit}
      />
    );
  }

  if (phase === 'intro') {
    return (
      <div className="dj-stage dj-screen">
        <p className="dj-hero-tag">🎧 Formato 03 · Prosodia</p>
        <h2 className="dj-h1"><span className="dj-glow">SHADOWING</span></h2>
        <p className="dj-lead">
          Escucha la línea a velocidad real. El texto está borroso a propósito:
          imita de <b>oído</b>, no de lectura. Luego habla encima, al mismo ritmo.
        </p>
        <p className="dj-caveat" style={{ marginBottom: 18 }}>
          Se puntúan <b>palabras</b> y <b>ritmo</b>. El reconocedor del navegador
          normaliza el acento, así que aquí no se mide pronunciación — y no vamos
          a decirte que sí.
        </p>
        <button type="button" className="dj-btn" style={{ width: '100%' }} onClick={begin}>
          Empezar ({deck.length} líneas) →
        </button>
      </div>
    );
  }

  const revealed = phase === 'scored' || plays >= 2;

  return (
    <>
      <FeedbackLayer flash={flash} floats={floats} />

      <div className="dj-hud">
        <button type="button" className="dj-quit" onClick={() => { cleanup(); onExit(); }}>← Salir</button>
        <span className="dj-badge">Shadowing</span>
        <span className="dj-spacer" />
        <span className="dj-progress">{item.ctx.replace('_', ' ')}</span>
        <span className="dj-score-badge">{i + 1}/{deck.length}</span>
      </div>

      <div className="dj-stage">
        {phase !== 'scored' ? (
          <>
            <button
              type="button"
              className={`dj-play ${phase === 'playing' ? 'dj-playing' : ''}`}
              onClick={play}
              disabled={!canSpeak || phase === 'live'}
              aria-label="Escuchar la línea"
            >
              ▶
            </button>
            <p className={`dj-blur ${revealed ? 'dj-clear' : ''}`}>{item.text}</p>
            <p className="dj-section-lbl">
              {plays === 0 ? 'Escúchala primero'
                : plays === 1 ? 'Otra vez y se revela el texto'
                : 'Ahora imítala encima'}
            </p>
            {phase === 'live' && <p className="dj-heard">{heard || 'Te escuchamos…'}</p>}
          </>
        ) : (
          <>
            <p className="dj-match dj-num" style={{ color: current.total >= 75 ? 'var(--dj-go)' : current.total >= 50 ? 'var(--dj-hot)' : 'var(--dj-no)' }}>
              {current.total}<small>%</small>
            </p>
            <p className="dj-grade-lbl" style={{ margin: '.4rem 0 0' }}>match de palabras y ritmo</p>

            <div className="dj-bars" style={{ width: '100%' }}>
              <div className="dj-bar-row">
                <span className="dj-bar-k">Palabras</span>
                <span className="dj-bar-t"><span className="dj-bar-f" style={{ width: `${current.words}%` }} /></span>
                <span className="dj-bar-v dj-num">{current.words}%</span>
              </div>
              <div className="dj-bar-row">
                <span className="dj-bar-k">Ritmo</span>
                <span className="dj-bar-t"><span className="dj-bar-f" style={{ width: `${current.tempo}%`, background: 'var(--dj-hot)' }} /></span>
                <span className="dj-bar-v dj-num">{current.tempo}%</span>
              </div>
            </div>

            <p className="dj-caveat">
              Nativo: {item.seconds.toFixed(1)}s · tú: {current.secs.toFixed(1)}s
            </p>

            <ul className="dj-linking">
              {item.linking.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </>
        )}
      </div>

      {phase === 'scored' ? (
        <button type="button" className="dj-btn dj-mic" onClick={next}>
          {i + 1 >= deck.length ? 'Ver resultado →' : 'Siguiente línea →'}
        </button>
      ) : phase === 'live' ? (
        <button type="button" className="dj-btn dj-mic dj-live" onClick={stopNow}>■ Terminé</button>
      ) : (
        <button type="button" className="dj-btn dj-mic" onClick={record} disabled={phase === 'playing' || plays === 0}>
          {plays === 0 ? 'Escucha primero' : '🎙 Hablar encima'}
        </button>
      )}
    </>
  );
}
